/**
 * "Saved to sketchbook — Bright rupture idea".
 *
 * A toast, at the bottom, that goes away by itself. Explicitly *not* a modal: the player
 * saved a sketch and is still sitting at the piano, so putting a dialog over the graph
 * would interrupt the exact activity the save was meant to preserve. The two things
 * someone might want next — rename it, look at it — are on the toast, and ignoring it is
 * the default.
 */

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCalmMotion } from './motionPreference.js';
import { CALM_FADE } from '../graph/motion.js';
import type { SaveReceipt } from './sessionView.js';

/** Long enough to read and act on, short enough not to be in the way. */
export const SAVE_TOAST_MS = 4000;

export interface SaveToastProps {
  receipt: SaveReceipt | null;
  onDismiss?: () => void;
  onRename?: (sketchId: string) => void;
  onView?: (sketchId: string) => void;
}

export function SaveToast({
  receipt,
  onDismiss,
  onRename,
  onView,
}: SaveToastProps): React.JSX.Element {
  const calm = useCalmMotion();
  const sketchId = receipt?.sketchId;

  useEffect(() => {
    if (!sketchId || !onDismiss) return;
    const timer = setTimeout(onDismiss, SAVE_TOAST_MS);
    return () => clearTimeout(timer);
  }, [sketchId, onDismiss]);

  return (
    <AnimatePresence>
      {receipt ? (
        <motion.div
          className="hc-toast"
          data-testid="hc-save-toast"
          role="status"
          initial={calm ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={calm ? { opacity: 0 } : { opacity: 0, y: 8 }}
          transition={calm ? CALM_FADE : { type: 'spring', stiffness: 420, damping: 36 }}
        >
          <span className="hc-toast__text">Saved to sketchbook — {receipt.title}</span>
          <span className="hc-toast__actions">
            {onRename ? (
              <button
                type="button"
                className="hc-toast__action"
                onClick={() => onRename(receipt.sketchId)}
              >
                Rename
              </button>
            ) : null}
            {onView ? (
              <button
                type="button"
                className="hc-toast__action"
                onClick={() => onView(receipt.sketchId)}
              >
                View
              </button>
            ) : null}
          </span>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
